import { getBuyRequestMeta, getSimpleForBuyRequest } from "@/js/helpers/buy-request"
import { IRogue, BuyRequest } from "@/js/services/RogueTypes"
import { RACK_COLORS } from "../../../constants"
import { RackOptions } from "../../../RackTypes"
import { getCustomMaterials } from "../../../utils/material-utils"
import { findBundleProduct, getAccessoryQuantity } from "../../../utils/product-lookup"
import { getDepthSelection, getPlateStorageSelection, getHeightSelection, getMiddleSelection, getSafetySelection, getJCupSelection, getFrontStabilizerSelection, getBandPegSelection, getBackStabilizerSelection, getBackSelection, getNamePlateMaterials, getTopCrossmemberMaterials, getFrontCrossmemberMaterials, getBenchSelection, getPlateSelection, getShelfSelections } from "../../../utils/selection-utils"
import { getBarConfig } from "../../bar/getBarConfig"
import { HybridRigBuilder } from "./hybridRigBuilder"
import { REAR_CROSSMEMBER_OPTION_TITLE } from "./hybridConstants"
import { getHybridWidth } from "./hybridUtils"

// Mirrors getBackSelection's classification but skips its isRM/isLatAndLowRow defaulting, which is meaningless per-unit.
function getUnitBack(rogue: IRogue, unitBuyRequest: BuyRequest): RackOptions['back'] {
  const selectedBack = findBundleProduct(rogue, unitBuyRequest, REAR_CROSSMEMBER_OPTION_TITLE)?.name

  if (selectedBack?.includes('Name')) return 'nameplate'
  if (selectedBack?.includes('Top Crossmember') || selectedBack?.includes('Beam')) return 'crossmember top 4'
  if (selectedBack?.includes('Fat/Skinny')) return 'fat skinny'

  return ''
}

function getWallBallTarget(rogue: IRogue, bundleBuyRequest: BuyRequest): RackOptions['wallBallTarget'] {
  const selected = findBundleProduct(rogue, bundleBuyRequest, 'wall ball', true)?.name

  if (!selected) return undefined

  return selected.toLowerCase().includes('double') ? 'double' : 'single'
}

export function createHybridRigConfig(rogue: IRogue, buyRequest: BuyRequest): RackOptions {
  const meta = getBuyRequestMeta(buyRequest, rogue)
  const rack = getSimpleForBuyRequest(buyRequest, rogue)
  const bar = findBundleProduct(rogue, buyRequest, 'Add a bar', true)

  const isRML = meta.name?.includes('RML') || meta.name?.includes('Monster Lite') || rack?.attributes.compat_tag === 'Monster Lite'
  const is390C = meta.name?.includes('RML-390')
  const isRML3 = meta.name?.includes('R-3')

  const line = isRML ? 'RML' : 'RM'
  const numbered = rack?.attributes.edition === 'Numbered' || rack?.attributes.upright_type === 'Numbered' || line === 'RM'
  const storage = !!meta.groupSku?.includes('RM-6') || !!meta.name?.includes('690')
  const color = RACK_COLORS[rack?.attributes.rig_color as string || 'Satin Black']

  const spotterArmsAccessory = getAccessoryQuantity(rogue, buyRequest, 'spotter arms')
  const strapSafetyAccessory = Boolean(getAccessoryQuantity(rogue, buyRequest, 'strap safety'))
  const multiGripAccessory = Boolean(getAccessoryQuantity(rogue, buyRequest, 'Multi Grip'))

  const materials = getCustomMaterials(rogue, buyRequest)

  // Set here rather than inside getCustomMaterials, which every other product shares - a 6-post rack's own Front Crossmember: row would otherwise start recoloring its crossmember too.
  const rackFrontMaterials = getFrontCrossmemberMaterials(rogue, buyRequest)
  if (materials && rackFrontMaterials) materials.frontCrossmember = rackFrontMaterials

  // Each unit reads its own crossmember type/color independently of the main rack's back selection; fat/skinny has no color variant.
  const getUnitConfig = (unit: BuyRequest) => {
    const back = getUnitBack(rogue, unit)

    let materials: ReturnType<typeof getNamePlateMaterials>
    if (back === 'nameplate') materials = getNamePlateMaterials(rogue, unit)
    else if (back === 'crossmember top 4') materials = getTopCrossmemberMaterials(rogue, unit)

    return {
      back,
      materials,
      frontMaterials : getFrontCrossmemberMaterials(rogue, unit),
      width          : getHybridWidth(rogue, unit),
      wallBallTarget : getWallBallTarget(rogue, unit),
      shelves        : getShelfSelections(rogue, unit),
    }
  }

  const units = buyRequest.children?.filter(child => child.metadata?.side) ?? []
  const leftUnits = units.filter(unit => unit.metadata?.side === 'left').map(getUnitConfig)
  const rightUnits = units.filter(unit => unit.metadata?.side === 'right').map(getUnitConfig)

  return {
    line,
    width           : getHybridWidth(rogue, buyRequest),
    wallBallTarget  : getWallBallTarget(rogue, buyRequest),
    shelves         : getShelfSelections(rogue, buyRequest),
    depth           : getDepthSelection(rack, meta),
    numbered,
    sixPost         : storage,
    plateStorage    : getPlateStorageSelection(rogue, buyRequest),
    color,
    height          : getHeightSelection(rack),
    hardware        : 'isonite',
    front           : 'crossmember top 4',
    middle          : isRML3 || is390C ? getMiddleSelection(multiGripAccessory) : undefined,
    back            : getBackSelection(rogue, buyRequest, meta),
    safeties        : getSafetySelection(rogue, buyRequest, meta, strapSafetyAccessory),
    jcup            : isRML3 ? getJCupSelection(rogue, buyRequest, true) : getJCupSelection(rogue, buyRequest),
    frontStabilizer : getFrontStabilizerSelection(rogue, buyRequest, meta),
    spotterArms     : spotterArmsAccessory,
    bandPegs        : getBandPegSelection(rogue, buyRequest),
    backStabilizer  : getBackStabilizerSelection(rogue, buyRequest, meta),
    bench           : getBenchSelection(rogue, buyRequest),
    bar             : bar ? getBarConfig(rogue, bar) : undefined,
    plates          : getPlateSelection(rogue, buyRequest),
    showEquipped    : Boolean(buyRequest.metadata?.showEquipped),
    accessories     : [],
    materials,
    isRML3,
    leftUnits,
    rightUnits,
  }
}

export function createHybridRig(rogue: IRogue, buyRequest: BuyRequest) {
  const config: RackOptions = createHybridRigConfig(rogue, buyRequest)
  const part = new HybridRigBuilder(config).build()

  return { config, part }
}